// src/lib/estimatePdf.js
// ───────────────────────────────────────────────────────────────────────────
// PDF de ESTIMADO para CLIENTE (HANDOFF-MULTIPIEZA §A3 / BRIEF). Llena el template
// branded de Cartonbar (Manual v2.2: negro dominante, onda de corrugado, 4 puntos
// proceso, slogan "WE SHAPE YOUR IDEAS") con los datos del estimado y lo manda a PDF
// vía la ventana de impresión del navegador (HTML/CSS → PDF, fuentes reales).
//
// Branding (template v2): logo oficial VECTORIAL inline (wordmark + 4 puntos, SVG, no PNG);
// flute de corrugado REAL (dos liners + onda continua apretada); display = Gobold REAL (otf
// oficial embebido como data-URL, @font-face) con Archivo Black de fallback; texto = Archivo
// (Google Fonts). HEX del manual.
//
// ⚠️ NUNCA exponer costo, markup ni margen — son internos (viven en Firestore). El PDF
// de cliente muestra solo unit price / qty / line total / subtotal / tax / total.
//
// buildEstimateHtml(d) es PURO y NO imprime → mismo HTML para el PREVIEW en pantalla y el PDF.
// downloadEstimatePdf añade el disparo de impresión (print:true). Así preview == PDF.
// ───────────────────────────────────────────────────────────────────────────
import { LOGO_MARK_SVG } from "../assets/marca/logoMark.mjs";
import { GOBOLD_REGULAR_DATAURL } from "../assets/marca/gobold.mjs";

const esc = (s) => String(s == null ? "" : s)
  .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
const money = (n) => `$${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// ── Diccionario ES/EN de labels + terms (C1 Fase 1). EN = default (comportamiento
//    histórico del PDF: cliente de Miami). El toggle del cotizador elige el idioma;
//    el slogan de marca ("WE SHAPE YOUR IDEAS") NO se traduce. Terms EN = BRIEF §5.
export const L10N = {
  en: {
    docTitle: "Estimate", title: "ESTIMATE", estimateNo: "Estimate No.",
    preparedFor: "Prepared for", date: "Date", preparedBy: "Prepared by",
    preparedByDefault: "Sales · Cartonbar",
    thDescription: "Description", thUnit: "Unit", thQty: "Qty", thLineTotal: "Line Total",
    subtotal: "Subtotal", salesTax: (p) => `Sales Tax (${p}%)`,
    grandTotal: "ESTIMATE TOTAL (USD)", termsTitle: "Terms &amp; Conditions",
    impositionTitle: "Imposition layout",
    startProject: "Start your project",
    terms: [
      ["Validity.", "Estimate valid for 15 days; pricing subject to review thereafter."],
      ["Scope.", "Includes custom operations and materials to complete the work as specified. Any spec change is re-quoted."],
      ["Artwork & proof.", "Production begins only after written approval of the final proof. Customer is responsible for final artwork approval."],
      ["Lead time.", "Standard production 3–5 business days after proof approval and payment. Rush available on request."],
      ["Over/under run.", "Custom runs subject to ±10% over/under; final invoice adjusted to quantity produced."],
      ["Additional work.", "Added design or custom requirements billed separately."],
      ["Payment.", "Credit terms require an approved credit application prior to PO processing; otherwise payment due on or before delivery."],
      ["Shipping.", "Freight/delivery quoted separately, subject to destination."],
      ["Taxes.", "Prices exclude applicable sales tax."],
      ["Sustainability.", "Materials 100% recyclable in the paper stream; FSC-certifiable stock on request."],
    ],
  },
  es: {
    docTitle: "Estimado", title: "ESTIMADO", estimateNo: "Estimado No.",
    preparedFor: "Preparado para", date: "Fecha", preparedBy: "Preparado por",
    preparedByDefault: "Ventas · Cartonbar",
    thDescription: "Descripción", thUnit: "Unitario", thQty: "Cant.", thLineTotal: "Total línea",
    subtotal: "Subtotal", salesTax: (p) => `Impuesto de venta (${p}%)`,
    grandTotal: "TOTAL DEL ESTIMADO (USD)", termsTitle: "Términos y condiciones",
    impositionTitle: "Imposición",
    startProject: "Empieza tu proyecto",
    terms: [
      ["Validez.", "Estimado válido por 15 días; el precio queda sujeto a revisión después."],
      ["Alcance.", "Incluye las operaciones y materiales a la medida para completar el trabajo según lo especificado. Cualquier cambio de especificación se recotiza."],
      ["Arte y prueba.", "La producción comienza solo tras la aprobación por escrito de la prueba final. El cliente es responsable de la aprobación del arte final."],
      ["Tiempo de entrega.", "Producción estándar de 3–5 días hábiles tras la aprobación de la prueba y el pago. Urgencias disponibles a pedido."],
      ["Sobrante/faltante.", "Las corridas a la medida están sujetas a ±10%; la factura final se ajusta a la cantidad producida."],
      ["Trabajo adicional.", "El diseño agregado o los requerimientos especiales se facturan por separado."],
      ["Pago.", "Los términos de crédito requieren una solicitud de crédito aprobada antes de procesar la orden; de lo contrario, el pago vence en o antes de la entrega."],
      ["Envío.", "El flete/entrega se cotiza por separado, según el destino."],
      ["Impuestos.", "Los precios no incluyen el impuesto de venta aplicable."],
      ["Sostenibilidad.", "Materiales 100% reciclables en la corriente de papel; stock certificable FSC a pedido."],
    ],
  },
};

const CSS = `
@font-face{ font-family:"Gobold"; font-style:normal; font-weight:400;
  src:url("${GOBOLD_REGULAR_DATAURL}") format("opentype"); font-display:swap; }
:root{ --cyan:#1C9CD8; --magenta:#E40A7C; --yellow:#FCEE0F; --black:#1A1A1A; --kraft:#C8A165;
  --ink:#1A1A1A; --muted:#6b6b6b; --line:#e3e3e3;
  --display:"Gobold","Archivo Black","Arial Black",Arial,sans-serif; --text:"Archivo",Arial,Helvetica,sans-serif; }
*{box-sizing:border-box;margin:0;padding:0}
/* US Letter - margin de pagina en CERO (unica fuente de espaciado = padding del body).
   FIX 2026-07-05 (bug de solape en Cloud): el header/footer eran position:fixed y el
   contenido se separaba con el margin de @page - el Chromium shell de Cloud posiciona
   los fixed relativo al AREA DE CONTENIDO (no al papel, como Chrome desktop) y el header
   se montaba sobre el titulo ESTIMATE y el cliente. AHORA header y footer viven en el
   FLUJO NORMAL del body y el margin de @page es 0 (page.pdf() tambien pasa margin:0).
   Trade-off aceptado: en documentos multipagina el header/footer NO se repiten por
   pagina (el estimado real es de 1 pagina). */
@page{ size: letter portrait; margin: 0 }
/* Nota: este documento ya NO usa fondos de color a sangre, así que no depende de la opción
   "Gráficos de fondo" del diálogo de impresión. print-color-adjust queda solo por compatibilidad
   (puntos de color del logo, acento cyan del slogan). El navegador agrega sus propios encabezados/
   pies (fecha, URL, n.º de página); para ocultarlos hay que desmarcarlos en el diálogo (no es
   controlable 100% por CSS en Chrome). */
html,body{ background:#fff; overflow-x:hidden; -webkit-print-color-adjust:exact; print-color-adjust:exact; color-adjust:exact }
.page{ width:8.5in; min-height:10.98in; display:flex; flex-direction:column; margin:0 auto; background:#fff; font-family:var(--text);
  color:var(--ink); position:relative }
.pad{ padding:0 .62in; flex:1 0 auto }
/* Header en FLUJO NORMAL (sin position:fixed - ver nota de @page), BLANCO, sin banda
   negra: logo a la izquierda, datos de la empresa a la derecha. */
.top{ background:#fff; color:var(--ink); padding:.32in .62in .18in }
.top .row{ display:flex; justify-content:space-between; align-items:flex-start }
.top .logo svg.cb-logo{ height:46px; width:auto; display:block }
.top .logo .st1{ fill:var(--ink) }   /* wordmark + 4.º punto: de blanco (era para fondo negro) a tinta */
.corp{ text-align:right; font-size:9.5px; line-height:1.5; color:var(--muted) }
.corp b{ color:var(--ink); font-size:10.5px; letter-spacing:.3px }
.titlebar{ display:flex; align-items:flex-end; justify-content:space-between; padding:.36in 0 .1in }
.titlebar h1{ font-family:var(--display); font-size:32px; letter-spacing:-.5px; line-height:1 }
.titlebar .num{ text-align:right }
.titlebar .num .lbl{ font-size:9px; letter-spacing:2px; color:var(--muted); text-transform:uppercase }
.titlebar .num .val{ font-family:var(--display); font-size:20px; color:var(--magenta) }
.meta{ display:flex; gap:.5in; padding:.12in 0 .26in; border-bottom:2px solid var(--black) }
.meta .col{ flex:1 } .meta .col.sm{ max-width:1.9in }
.meta .lbl{ font-size:9px; letter-spacing:1.5px; text-transform:uppercase; color:var(--cyan); font-weight:700; margin-bottom:4px }
.meta .who{ font-size:11px; line-height:1.5 } .meta .who b{ font-size:12.5px }
table{ width:100%; border-collapse:collapse; margin-top:.18in }
thead th{ font-size:9px; letter-spacing:1px; text-transform:uppercase; color:var(--muted);
  text-align:left; padding:8px 6px; border-bottom:1.5px solid var(--black) }
thead th.r{ text-align:right }
tbody td{ padding:11px 6px; border-bottom:1px solid var(--line); vertical-align:top; font-size:11px }
tbody td.r{ text-align:right; white-space:nowrap }
tbody .desc b{ font-size:11.5px; letter-spacing:.2px }
tbody .desc small{ display:block; color:var(--muted); font-size:9.5px; line-height:1.45; margin-top:2px }
tbody tr:last-child td{ border-bottom:1.5px solid var(--black) }
.totals{ display:flex; justify-content:flex-end; margin-top:.18in }
.totals .box{ width:3.1in }
.totals .ln{ display:flex; justify-content:space-between; font-size:11px; padding:6px 2px; color:#333 }
/* Total con énfasis SIN fondo a sangre: recuadro con doble línea (tinta). Vive dentro de .pad,
   así respeta los márgenes internos y nunca toca el borde de la hoja. */
.totals .grand{ border-top:2.5px solid var(--ink); border-bottom:2.5px solid var(--ink); color:var(--ink);
  padding:11px 4px; display:flex; justify-content:space-between; align-items:center; margin-top:6px }
.totals .grand .t{ font-family:var(--display); font-size:13px; letter-spacing:.5px }
.totals .grand .v{ font-family:var(--display); font-size:22px; color:var(--ink) }
.impos{margin-top:.18in} .impos h3{font-size:10px;letter-spacing:1.5px;text-transform:uppercase;color:var(--cyan);margin-bottom:6px}
.imporow{display:flex;gap:.15in;flex-wrap:wrap} .impo{flex:1 1 2.4in;max-width:3.4in} .impo svg{width:100%;height:auto;display:block;border:1px solid var(--line)}
.impo .cap{font-size:9px;color:var(--muted);margin-top:2px}
.terms{ margin-top:.3in; border-top:1px solid var(--line); padding-top:.16in }
.terms h3{ font-size:10px; letter-spacing:2px; text-transform:uppercase; color:var(--black); margin-bottom:8px }
.terms ol{ margin-left:14px; columns:2; column-gap:.4in }
.terms li{ font-size:8.7px; line-height:1.5; color:#444; margin-bottom:5px; break-inside:avoid }
/* Footer en FLUJO NORMAL, empujado al pie de la pagina unica por el flex de .page
   (margin-top:auto). BLANCO, separado por linea superior. */
.foot{ margin-top:auto; background:#fff; color:var(--ink);
  border-top:1.5px solid var(--ink); padding:.16in .62in; display:flex; justify-content:space-between; align-items:center }
.foot .slogan{ font-family:var(--display); font-size:18px; letter-spacing:.5px; color:var(--ink) }
.foot .slogan span{ color:var(--cyan) }
.foot .right{ text-align:right; font-size:9px; line-height:1.6; color:var(--muted) } .foot .right b{ color:var(--ink) }
.watermark{ position:absolute; right:-40px; top:42%; font-size:240px; color:#000; opacity:.025;
  font-family:var(--display); pointer-events:none; transform:rotate(-8deg) }
/* Solo por compatibilidad (ya no hay fondos a sangre): asegura que los puntos de color del logo
   y el acento cyan se impriman igual que en pantalla, sin depender de "Gráficos de fondo". */
.page,.top,.foot,.totals .grand{ -webkit-print-color-adjust:exact; print-color-adjust:exact; color-adjust:exact }
@media print{ html,body,.page,.top,.foot,.totals .grand{ -webkit-print-color-adjust:exact; print-color-adjust:exact; color-adjust:exact } }
`;


/**
 * HTML completo del estimado de cliente (branded, EN). PURO/testeable.
 * @param {object} d  { estimateNumber, date, clientName, clientLines[], preparedBy, lang?,
 *                      lines:[{description, specs, unit, qty, lineTotal}], subtotal, taxPct, tax, total }
 */
export function buildEstimateHtml(d = {}) {
  // C1 (Fase 1): idioma de los LABELS del PDF. 'en' default (comportamiento histórico).
  const lang = d.lang === "es" ? "es" : "en";
  const L = L10N[lang];
  const lines = (d.lines || []).map((li) => `<tr>
    <td class="desc"><b>${esc(li.description)}</b>${li.specs ? `<small>${esc(li.specs)}</small>` : ""}</td>
    <td class="r">${money(li.unit)}</td><td class="r">${Number(li.qty || 0)}</td><td class="r">${money(li.lineTotal)}</td>
  </tr>`).join("");
  const clientLines = (d.clientLines || []).map(esc).join("<br>");
  const imposiciones = (d.imposiciones || []).filter((im) => im && im.svg).map((im) =>
    `<div class="impo">${im.svg}<div class="cap">${esc(im.name || "")}</div></div>`).join("");
  const terms = L.terms.map(([h, t]) => `<li><b>${h}</b> ${t}</li>`).join("");
  const taxPct = d.taxPct != null ? d.taxPct : 7;
  const printScript = d.print
    ? `<script>window.onload=function(){setTimeout(function(){try{window.focus();window.print();}catch(e){}},400);};</scr`+`ipt>`
    : "";
  return `<!doctype html><html lang="${lang}"><head><meta charset="utf-8">
<title>Cartonbar — ${L.docTitle} ${esc(d.estimateNumber || "")}</title>
<style>@import url('https://fonts.googleapis.com/css2?family=Archivo:wght@400;500;700&family=Archivo+Black&display=swap');${CSS}</style></head>
<body><div class="page">
  <div class="top"><div class="row">
    <div class="logo">${LOGO_MARK_SVG}</div>
    <div class="corp"><b>MAIA SERVICES CORP DBA CARTONBAR</b><br>7315 NW 44th St · Miami, FL 33166<br>(305) 418-0365 · cartonbar.com</div>
  </div></div>
  <div class="pad">
    <div class="watermark">∞</div>
    <div class="titlebar"><h1>${L.title}</h1>
      <div class="num"><div class="lbl">${L.estimateNo}</div><div class="val">${esc(d.estimateNumber || "—")}</div></div></div>
    <div class="meta">
      <div class="col"><div class="lbl">${L.preparedFor}</div><div class="who"><b>${esc(d.clientName || "—")}</b>${clientLines ? `<br>${clientLines}` : ""}</div></div>
      <div class="col sm"><div class="lbl">${L.date}</div><div class="who">${esc(d.date || "")}</div>
        <div class="lbl" style="margin-top:10px">${L.preparedBy}</div><div class="who">${esc(d.preparedBy || L.preparedByDefault)}</div></div>
    </div>
    <table><thead><tr><th>${L.thDescription}</th><th class="r">${L.thUnit}</th><th class="r">${L.thQty}</th><th class="r">${L.thLineTotal}</th></tr></thead>
      <tbody>${lines}</tbody></table>
    <div class="totals"><div class="box">
      <div class="ln"><span>${L.subtotal}</span><span>${money(d.subtotal)}</span></div>
      <div class="ln"><span>${L.salesTax(taxPct)}</span><span>${money(d.tax)}</span></div>
      <div class="grand"><span class="t">${L.grandTotal}</span><span class="v">${money(d.total)}</span></div>
    </div></div>
    ${imposiciones ? `<div class="impos"><h3>${L.impositionTitle}</h3><div class="imporow">${imposiciones}</div></div>` : ""}
    <div class="terms"><h3>${L.termsTitle}</h3><ol>${terms}</ol></div>
  </div>
  <div class="foot"><div class="slogan">WE SHAPE YOUR <span>IDEAS</span></div>
    <div class="right"><b>${L.startProject}</b><br>cartonbar.com · (305) 418-0365</div></div>
</div>
${printScript}
</body></html>`;
}

/**
 * Abre el estimado en una ventana de impresión (navegador → Guardar como PDF).
 * Usa el MISMO buildEstimateHtml que el preview (+ print:true para disparar la impresión) → preview == PDF.
 */
export function downloadEstimatePdf(data) {
  const html = buildEstimateHtml({ ...data, print: true });
  const w = window.open("", "_blank");
  if (!w) throw new Error("El navegador bloqueó la ventana de impresión (permití pop-ups).");
  w.document.open(); w.document.write(html); w.document.close();
}

/**
 * Descarga un PDF (base64, respuesta de la CF renderEstimatePdf) como archivo. CLIENTE-only.
 * Slice 1 Tiers-PDF: el PDF ya viene renderizado del SERVIDOR (Puppeteer, US Letter,
 * printBackground) y persistido en Storage — acá solo se dispara la descarga local.
 * Reemplaza el flujo window.print()/downloadEstimatePdf (que queda como legacy sin callers).
 */
export function downloadPdfBase64(base64, filename) {
  const bytes = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
  const url = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
  const a = document.createElement('a');
  a.href = url; a.download = filename || 'estimate.pdf';
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}


/**
 * SVG (string puro) de la imposición sobre la hoja — port fiel de ImpositionSVG.js
 * (cut rojo / crease ámbar, nesting centrado o grilla 1UP), SIN las ondas de flauta
 * (herramienta interna de validación; el PDF de cliente va limpio). PURO: corre en
 * el navegador (preview) y en la CF (Puppeteer). Slice 1 PLAN-MATERIAL-SOURCING.
 * @param {object} geo    geometry del motor ({ sheetW, sheetH, mode, nx, ny, deg, blankX, blankY, gap })
 * @param {Array}  polys  polilíneas del DXF ({ color, pts:[[x,y],...] })
 */
export function impositionSvgString(geo, polys) {
  if (!geo || !geo.sheetW || !geo.sheetH || !polys || !polys.length) return "";
  const strokeOf = (c) => (c === 2 ? "#d97706" : "#dc2626");
  const rot90p = (pts, bx) => pts.map(([x, y]) => [y, bx - x]);
  const toPts = (pts, ox, oy, h) => pts.map(([x, y]) => (x + ox).toFixed(3) + "," + (h - (y + oy)).toFixed(3)).join(" ");
  const bb = { minx: Infinity, miny: Infinity, maxx: -Infinity, maxy: -Infinity };
  polys.forEach((p) => p.pts.forEach(([x, y]) => {
    if (x < bb.minx) bb.minx = x; if (y < bb.miny) bb.miny = y;
    if (x > bb.maxx) bb.maxx = x; if (y > bb.maxy) bb.maxy = y;
  }));
  const bbx = bb.maxx - bb.minx; const bby = bb.maxy - bb.miny;
  const { sheetW, sheetH, nx, ny, deg, blankX, blankY, gap = 0, mode } = geo;
  const sw = Math.max(sheetW, sheetH) / 320;
  const base = polys.map((p) => ({ color: p.color, pts: p.pts.map(([x, y]) => [x - bb.minx, y - bb.miny]) }));
  const out = [];
  if (mode === "nesting") {
    let pts0 = base; let fw = bbx; let fh = bby;
    if (bbx > sheetW + 0.05 && bby <= sheetW + 0.05) {
      pts0 = base.map((p) => ({ color: p.color, pts: rot90p(p.pts, bbx) }));
      fw = bby; fh = bbx;
    }
    const ox = Math.max(0, (sheetW - fw) / 2); const oy = Math.max(0, (sheetH - fh) / 2);
    pts0.forEach((p) => out.push('<polyline points="' + toPts(p.pts, ox, oy, sheetH) + '" fill="none" stroke="' + strokeOf(p.color) + '" stroke-width="' + sw + '"/>'));
  } else {
    const cell = (deg === 90) ? base.map((p) => ({ color: p.color, pts: rot90p(p.pts, bbx) })) : base;
    const bw = (deg === 90) ? blankY : blankX;
    const bh = (deg === 90) ? blankX : blankY;
    for (let r = 0; r < (ny || 1); r++) {
      for (let c = 0; c < (nx || 1); c++) {
        const ox = c * (bw + gap); const oy = r * (bh + gap);
        if (ox + bw > sheetW + 0.05 || oy + bh > sheetH + 0.05) continue;
        cell.forEach((p) => out.push('<polyline points="' + toPts(p.pts, ox, oy, sheetH) + '" fill="none" stroke="' + strokeOf(p.color) + '" stroke-width="' + (sw * 0.85) + '"/>'));
      }
    }
  }
  return '<svg viewBox="0 0 ' + sheetW + ' ' + sheetH + '" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid meet">'
    + '<rect x="0" y="0" width="' + sheetW + '" height="' + sheetH + '" fill="#ffffff" stroke="#94a3b8" stroke-width="' + sw + '"/>'
    + out.join("") + '</svg>';
}
